import React, { useEffect, useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import useInfiniteScroll from 'react-infinite-scroll-hook';

import JourneyList from 'modules/journey/list/List';

import { Flexer } from '@ontruck/orc';

import { AssignedJourneyCard } from 'modules/journey/card';
import { EmptyListStyled, LoadingListStyled } from 'modules/journey/list/styled/index.js';
import { DateTitleWithCost } from 'modules/journey/card/headings';

import { DispatchingUsers, DispatchingContextProvider } from 'modules/ownership-dispatching';

import ScrollElementContext from 'containers/App/context';

import { openJourneyDetail } from 'redux/modules/journeys/effects';

const Assigned = React.memo(
  ({
    history,
    onLoadMore,
    filters,
    nextPage,
    hasMore,
    className,
    loading,
    hasError,
    grouped,
    intl,
    isDispatcher,
    nextPageLoading,
  }) => {
    const t = intl.messages.containers.Jobs;
    const hasJourneys = !!Object.keys(grouped).length;
    const loadingJourneys = loading || nextPageLoading;
    const dispatch = useDispatch();

    const scrollElement = useContext(ScrollElementContext);

    const handleJourneyClick = useCallback(
      (id) => {
        const listScrollTop = scrollElement.current.scrollTop;

        dispatch(
          openJourneyDetail({
            id,
            fromList: 'assigned',
            listScrollTop,
          })
        );
      },
      [scrollElement, dispatch]
    );

    const handleLoadMore = useCallback(() => {
      onLoadMore({ filters, nextPage });
    }, [filters, onLoadMore, nextPage]);

    const infiniteRef = useInfiniteScroll({
      loading: nextPageLoading,
      hasNextPage: !!nextPage,
      onLoadMore: handleLoadMore,
      threshold: 200,
    });

    useEffect(() => {
      const { action } = history;

      if (!['PUSH'].includes(action)) {
        return;
      }

      const { location } = history;

      const { listScrollTop } = (location || {}).state || {};

      scrollElement.current.scrollTop = listScrollTop;
    }, [history, scrollElement]);

    const journeyList = (
      <>
        {!loading && hasJourneys && (
          <div ref={infiniteRef}>
            <JourneyList
              grouped={grouped}
              titleComponent={DateTitleWithCost}
              loadingComponent={LoadingListStyled}
              cardComponent={AssignedJourneyCard}
              onCardClick={handleJourneyClick}
            />
          </div>
        )}

        {!loading && !hasJourneys && !hasError && <EmptyListStyled>{t.no_jobs}</EmptyListStyled>}
        {hasError && <EmptyListStyled>There was a problem getting the jobs</EmptyListStyled>}
        {loadingJourneys && <LoadingListStyled />}
      </>
    );

    if (!isDispatcher) {
      return (
        <DispatchingContextProvider>
          <>{journeyList}</>
        </DispatchingContextProvider>
      );
    }

    return (
      <DispatchingContextProvider>
        <Flexer.Parent className={className}>
          <Flexer.Child flex="1" justifyContent="center" mRight="M">
            {journeyList}
          </Flexer.Child>
          <Flexer.Child style={{ width: 300 }}>
            <DispatchingUsers />
          </Flexer.Child>
        </Flexer.Parent>
      </DispatchingContextProvider>
    );
  }
);

export default Assigned;
